import React, { useEffect, useState } from 'react';
import {
  Table,
  Space,
  Button,
  Avatar,
  Tag,
  Popconfirm,
  Modal,
  Input,
  Grid,
  Typography,
  List,
  Card,
  message,
  Select,
} from 'antd';
import {
  UsergroupAddOutlined,
  DeleteOutlined,
  EditOutlined,
  WhatsAppOutlined,
} from '@ant-design/icons';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { API_BASE } from '../../config/api';

const { useBreakpoint } = Grid;
const { Search } = Input;
const { Title } = Typography;

const API_USERS = `${API_BASE}/users`;
const API_UPDATE_TYPE = `${API_BASE}/user`;

const buildWhatsAppLink = (phone) => {
  const digits = String(phone || '').replace(/\D/g, '');
  if (!digits) {
    return null;
  }

  return `https://wa.me/${digits}`;
};

const renderUserAvatar = (user) => (
  <Avatar
    src={user?.avatar || undefined}
    style={{
      backgroundColor: user?.avatar ? undefined : user?.background_avatar || '#1677ff',
      color: '#fff',
      fontWeight: 700,
      flexShrink: 0,
    }}
  >
    {user?.name?.charAt(0)?.toUpperCase() || 'U'}
  </Avatar>
);

const Users = () => {
  const navigate = useNavigate();
  const screens = useBreakpoint();
  const isMobile = !screens.md;

  const [usuarios, setUsuarios] = useState([]);
  const [usuariosFiltrados, setUsuariosFiltrados] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState('');

  // modal actualizar
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [usuarioSeleccionado, setUsuarioSeleccionado] = useState(null);
  const [editForm, setEditForm] = useState({
    name: '',
    lastName: '',
    email: '',
    phone: '',
    type_client: 'new',
  });

  useEffect(() => {
    fetchUsers();
  }, []);

  // =========================
  // FETCH USERS
  // =========================
  const fetchUsers = async () => {
    setLoading(true);
    const token = localStorage.getItem('token');

    try {
      const res = await axios.get(API_USERS, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setUsuarios(res.data.users);
      setUsuariosFiltrados(res.data.users);
    } catch (error) {
      Modal.error({
        title: 'Error al cargar usuarios',
        content: error.response?.data?.message || error.message,
      });
    } finally {
      setLoading(false);
    }
  };

  // =========================
  // SEARCH
  // =========================
  const handleSearch = (value) => {
    const text = value.toLowerCase();
    setSearchText(text);

    const filtered = usuarios.filter((u) =>
      `${u.name} ${u.lastName}`.toLowerCase().includes(text) ||
      u.email.toLowerCase().includes(text)
    );

    setUsuariosFiltrados(filtered);
  };

  // =========================
  // DELETE USER
  // =========================
  const eliminarUsuario = async (id) => {
    const token = localStorage.getItem('token');

    try {
      await axios.delete(`${API_UPDATE_TYPE}/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      message.success('Usuario eliminado');
      fetchUsers();
    } catch (error) {
      Modal.error({
        title: 'Error al eliminar usuario',
        content: error.response?.data?.message || error.message,
      });
    }
  };

  // =========================
  // UPDATE TYPE CLIENT
  // =========================
  const actualizarUsuario = async () => {
    const token = localStorage.getItem('token');

    try {
      await axios.patch(
        `${API_UPDATE_TYPE}/${usuarioSeleccionado?.id}`,
        {
          name: editForm.name,
          lastName: editForm.lastName,
          email: editForm.email,
          phone: editForm.phone,
          type_client: editForm.type_client,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      message.success('Usuario actualizado');
      setIsModalVisible(false);
      fetchUsers();
    } catch (error) {
      Modal.error({
        title: 'Error actualizando usuario',
        content: error.response?.data?.message || error.message,
      });
    }
  };

  const openEditModal = (user) => {
    setUsuarioSeleccionado(user);
    setEditForm({
      name: user.name || '',
      lastName: user.lastName || '',
      email: user.email || '',
      phone: user.phone || '',
      type_client: user.type_client || 'new',
    });
    setIsModalVisible(true);
  };

  // =========================
  // TABLE COLUMNS
  // =========================
  const columns = [
    {
      title: 'Usuario',
      key: 'usuario',
      render: (_, record) => (
        <Space>
          {renderUserAvatar(record)}
          <span>{record.name} {record.lastName}</span>
        </Space>
      ),
      sorter: (a, b) => a.name.localeCompare(b.name),
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: 'WhatsApp',
      dataIndex: 'phone',
      key: 'phone',
      render: (phone) => phone || '—',
    },
    {
      title: 'Contraseña',
      dataIndex: 'admin_visible_password',
      key: 'admin_visible_password',
      render: (password) => (
        password ? (
          <Tag color="purple" style={{ fontFamily: 'monospace' }}>
            {password}
          </Tag>
        ) : (
          <Tag>—</Tag>
        )
      ),
    },
    {
      title: 'Tipo',
      dataIndex: 'type_client',
      key: 'type_client',
      render: (type) => (
        <Tag color={type === 'reliable' ? 'green' : 'orange'}>
          { type === 'reliable'? 'Confiable' : 'Nuevo'}
        </Tag>
      ),
    },
    {
      title: 'Acciones',
      key: 'acciones',
      render: (_, record) => (
        <Space>
          <Button
            size="small"
            type="text"
            icon={<WhatsAppOutlined style={{ color: '#25D366', fontSize: 18 }} />}
            disabled={!record.phone}
            onClick={() => {
              const url = buildWhatsAppLink(record.phone);
              if (url) {
                window.open(url, '_blank', 'noopener,noreferrer');
              }
            }}
          />

          <Button
            size="small"
            type="primary"
            onClick={() => navigate(`/admin/nueva-reservacion?userId=${record.id}`)}
          >
            Reservar
          </Button>

          <Button
            size="small"
            icon={<EditOutlined />}
            onClick={() => openEditModal(record)}
          >
            Editar
          </Button>

          <Popconfirm
            title="¿Eliminar usuario?"
            onConfirm={() => eliminarUsuario(record.id)}
            okText="Sí"
            cancelText="No"
          >
            <Button
              danger
              size="small"
              icon={<DeleteOutlined />}
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      {/* HEADER */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 24 }}>
        <UsergroupAddOutlined style={{ fontSize: 28, color: '#1890ff' }} />
        <Title level={3} style={{ margin: 0 }}>
          Gestión de Usuarios
        </Title>
      </div>

      {/* SEARCH */}
      <Search
        placeholder="Buscar por nombre o email"
        allowClear
        enterButton="Buscar"
        size="large"
        value={searchText}
        onChange={(e) => setSearchText(e.target.value)}
        onSearch={handleSearch}
        style={{ maxWidth: 500, marginBottom: 16 }}
      />

      {/* MOBILE */}
      {isMobile ? (
        <List
          loading={loading}
          dataSource={usuariosFiltrados}
          renderItem={(item) => (
            <Card
              style={{ marginBottom: 16 }}
              title={
                <Space>
                  {renderUserAvatar(item)}
                  <span>{item.name} {item.lastName}</span>
                </Space>
              }
              extra={
                <Space>
                  <Button
                    size="small"
                    type="text"
                    icon={<WhatsAppOutlined style={{ color: '#25D366', fontSize: 18 }} />}
                    disabled={!item.phone}
                    onClick={() => {
                      const url = buildWhatsAppLink(item.phone);
                      if (url) {
                        window.open(url, '_blank', 'noopener,noreferrer');
                      }
                    }}
                  />
                  <Button
                    size="small"
                    type="primary"
                    onClick={() => navigate(`/admin/nueva-reservacion?userId=${item.id}`)}
                  >
                    Reservar
                  </Button>
                  <Button
                    size="small"
                    onClick={() => openEditModal(item)}
                  >
                    Editar
                  </Button>
                  <Popconfirm
                    title="¿Eliminar usuario?"
                    onConfirm={() => eliminarUsuario(item.id)}
                    okText="Sí"
                    cancelText="No"
                  >
                    <Button
                      danger
                      size="small"
                      icon={<DeleteOutlined />}
                    />
                  </Popconfirm>
                </Space>
              }
            >
              <p><strong>Email:</strong> {item.email}</p>
              <p><strong>WhatsApp:</strong> {item.phone || '—'}</p>
              <p>
                <strong>Contraseña:</strong>{" "}
                {item.admin_visible_password ? (
                  <Tag color="purple" style={{ fontFamily: 'monospace' }}>
                    {item.admin_visible_password}
                  </Tag>
                ) : "—"}
              </p>
              <p><Tag>{item.type_client}</Tag></p>
            </Card>
          )}
        />
      ) : (
        <Table
          dataSource={usuariosFiltrados}
          columns={columns}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 10 }}
        />
      )}

      {/* MODAL UPDATE */}
      <Modal
        title="Editar usuario"
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        onOk={actualizarUsuario}
        okText="Guardar"
        cancelText="Cancelar"
      >
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
          <div>
            <p style={{ marginBottom: 6 }}><strong>Nombre</strong></p>
            <Input
              value={editForm.name}
              onChange={(e) =>
                setEditForm((current) => ({ ...current, name: e.target.value }))
              }
            />
          </div>
          <div>
            <p style={{ marginBottom: 6 }}><strong>Apellido</strong></p>
            <Input
              value={editForm.lastName}
              onChange={(e) =>
                setEditForm((current) => ({ ...current, lastName: e.target.value }))
              }
            />
          </div>
          <div>
            <p style={{ marginBottom: 6 }}><strong>Email</strong></p>
            <Input
              value={editForm.email}
              onChange={(e) =>
                setEditForm((current) => ({ ...current, email: e.target.value }))
              }
            />
          </div>
          <div>
            <p style={{ marginBottom: 6 }}><strong>WhatsApp</strong></p>
            <Input
              value={editForm.phone}
              onChange={(e) =>
                setEditForm((current) => ({ ...current, phone: e.target.value }))
              }
            />
          </div>
          <div>
            <p style={{ marginBottom: 6 }}><strong>Tipo de cliente</strong></p>
            <Select
              value={editForm.type_client}
              onChange={(value) =>
                setEditForm((current) => ({ ...current, type_client: value }))
              }
              style={{ width: '100%' }}
            >
              <Select.Option value="new">Nuevo</Select.Option>
              <Select.Option value="reliable">Confiable</Select.Option>
            </Select>
          </div>
        </Space>
      </Modal>
    </div>
  );
};

export default Users;
